import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { KeyValuePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatInput } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateModule } from '@ngx-translate/core';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { OccasionGeniusEvent } from 'src/app/core/types/models/occasion-genius-event.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';
import { IOccasionGeniusRecurringEvent } from 'src/app/core/types/interfaces/occasion-genius-event.interface';
import {
  BOTTOM_LEFT_POSITION,
  OverlayPanel,
} from 'src/app/core/services/overlay-panel';

import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';

import { SwiperDirective } from '../../directives/swiper.directive';

import { NoItemsFoundComponent } from '../no-items-found/no-items-found.component';
import { DateRangePanelComponent } from '../date-range-panel/date-range-panel.component';
import { OccasionGeniusCardComponent } from '../occasion-genius-card/occasion-genius-card.component';
import { ViatorGroupComponent } from '../../used-components/viator/viator-group/viator-group.component';

import { Virtual } from 'swiper/modules';

import moment from 'moment';

@Component({
  selector: 'app-occasion-genius-with-filters',
  templateUrl: './occasion-genius-with-filters.component.html',
  styleUrls: ['./occasion-genius-with-filters.component.scss'],
  imports: [
    TranslateModule,
    NgxSkeletonLoaderModule,
    MatIconModule,
    OccasionGeniusCardComponent,
    MatButtonModule,
    ViatorGroupComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDatepickerModule,
    SwiperDirective,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatLabel,
    MatFormField,
    MatInput,
    FormsModule,
    NoItemsFoundComponent,
    KeyValuePipe,
    MatCheckbox,
  ],
  providers: [OccasionGeniusService],
})
export class OccasionGeniusWithFiltersComponent implements OnChanges {
  @Input() events: Array<OccasionGeniusEvent | IOccasionGeniusRecurringEvent> =
    [];
  @Input({ required: true }) cityId: number;
  @Input() cityName: string = '';
  @Input() countryName: string = '';
  @Input() startDate: string;
  @Input() endDate: string;
  @Input() isFetching: boolean = false;
  @Input() occasionGeniusResponse: OccasionGeniusResponse =
    new OccasionGeniusResponse();

  public showFilters: boolean = true;
  public filters: {
    name: string;
    count: number;
    selected: boolean;
    events: Array<OccasionGeniusEvent | IOccasionGeniusRecurringEvent>;
  }[] = [];
  public selectedFilters: string[] = [];
  public selectedScoreValue: { label: string; value: string[] } = {
    label: '',
    value: [],
  };
  public scores = [
    { label: '🌟 Intimate events', value: ['0', '1'] },
    { label: '🔥 Medium-sized events', value: ['2', '3'] },
    { label: '🌍 Large events', value: ['4', '5', '6'] },
  ];

  public fetchingItems: number[] = new Array(2);
  public fetchingFilters: number[] = new Array(5);
  public readonly m = moment;

  public readonly eSvgIcons = ESvgIcons;

  @ViewChildren(SwiperDirective) swipers!: QueryList<SwiperDirective>;
  public swiperOptions: SwiperOptions = {
    modules: [Virtual],
    lazyPreloadPrevNext: 2,
    loop: false,
    slidesPerView: 'auto',
    spaceBetween: 24,
    breakpoints: {
      320: {
        slidesPerView: 'auto',
        spaceBetween: 24,
      },
    },
    navigation: {
      disabledClass: 'rpf-swiper-btn-disabled',
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
  };

  #overlayPanelService = inject(OverlayPanel);

  constructor(
    protected breakpointService: BreakpointService,
    private occasionGeniusService: OccasionGeniusService,
    private cdr: ChangeDetectorRef,
  ) {}

  getCardEvent(event: any): OccasionGeniusEvent[] {
    return event?.events?.length ? event.events : [event];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.occasionGeniusResponse?.currentValue.length) {
      this.swipers.toArray().forEach((swiper) => {
        swiper.updateSwiper();
      });
      this.cdr.markForCheck();
    }
  }

  onChangeScore(value: { label: string; value: string[] }): void {
    const defaultValue = { label: '', value: [] };
    this.selectedScoreValue =
      this.selectedScoreValue === value ? defaultValue : value;

    this.occasionGeniusResponse.updateScoreFilters(
      this.selectedScoreValue.value.length ? this.selectedScoreValue.value : [],
    );

    this.swipers.toArray().forEach((swiper) => {
      swiper.updateSwiper();
    });
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
    this.cdr.markForCheck();
  }

  onFilterChange() {
    this.swipers.toArray().forEach((swiper) => {
      swiper.updateSwiper();
    });
    this.cdr.markForCheck();
  }

  parseFlags(flagString: string): any[] {
    return flagString.replace(/"/g, '').split(',');
  }

  formatFlagName(flag): string {
    return flag.replace(/_/g, ' ');
  }

  groupEventsByFlags(events): any {
    const groupedFlags = {};

    events.forEach((event) => {
      const flags = this.parseFlags(event.flags);

      flags.forEach((flag) => {
        if (!groupedFlags[flag]) {
          groupedFlags[flag] = { count: 0, events: [] };
        }
        groupedFlags[flag].count++;
        groupedFlags[flag].events.push(event);
      });
    });

    return groupedFlags;
  }

  public fetchEventsWithFilters(): void {
    this.isFetching = true;
    this.cdr.markForCheck();

    this.occasionGeniusService
      .getOccasionGeniusAll(this.cityId, {
        from: moment(this.startDate).unix(),
        to: moment(this.endDate).unix(),
        offset: 0,
      })
      .subscribe((response) => {
        const occasionGeniusResponse = new OccasionGeniusResponse(response);
        this.occasionGeniusResponse.updateEvents(occasionGeniusResponse.events);
        this.isFetching = false;
        this.cdr.markForCheck();

        this.swipers.toArray().forEach((swiper) => {
          swiper.updateSwiper();
        });
        // this.ssrHelperService.updateSwiper(this.#swiper);
      });
  }

  public onSearchQueryChange(): void {
    this.cdr.markForCheck();
  }

  public showDateRangePanel(inputElement) {
    const panelRef = this.#overlayPanelService.open(DateRangePanelComponent, {
      origin: inputElement,
      position: BOTTOM_LEFT_POSITION,
      data: {
        showTypeSelector: false,
        tripStartDate: this.startDate,
        tripEndDate: this.endDate,
      },
    });
    const subs = panelRef
      .valueChanged()
      .subscribe((data: { tripStartDate; tripEndDate }) => {
        this.startDate = data.tripStartDate;
        this.endDate = data.tripEndDate;
        this.fetchEventsWithFilters();
      });
    panelRef.afterClosed().subscribe(() => subs.unsubscribe());
  }

  public getDateValue(): string {
    return (
      new Date(this.startDate).toDateString() +
      ' - ' +
      new Date(this.endDate).toDateString()
    );
  }
}
